import { Rnd } from 'react-rnd';
import { useDebounceEffect, useEventListener } from 'ahooks';
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { cropImage } from './utils';
import { useTempState, setProjectState } from '@starflow/app/store';
import './crop.scss';
import { eventBus } from '@starflow/app/helpers';

const resizeHandleClasses = {
  bottom: 'bottom',
  bottomLeft: 'bottom-left',
  bottomRight: 'bottom-right',
  left: 'left',
  right: 'right',
  top: 'top',
  topLeft: 'top-left',
  topRight: 'top-right',
};

let prevScale = null;
let cropperTimmer = null;

export const ImageCropTool = React.memo(({ element, scale }) => {
  const width = element.width * element.scaleX * scale;
  const height = element.height * element.scaleY * scale;

  const [hideCropper, setHideCropper] = useState(false);
  const [cropRect, setCropRect] = useState({ key: 1, x: 0, y: 0, width, height });

  const tempRef = useRef({});
  const cropRectRef = useRef(cropRect);

  const handleApplyCrop = useCallback(() => {
    const { willCancelCrop, id, width, height } = tempRef.current;

    prevScale = null;

    if (willCancelCrop) return;

    const isCropped = cropRectRef.current.width < width || cropRectRef.current.height < height;

    if (isCropped) {
      cropImage(id, cropRectRef.current, element);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        tempRef.current.willCancelCrop = true;
        setProjectState({
          currentElementTool: null,
        });
      } else if (event.key === 'Enter') {
        handleApplyCrop();
        tempRef.current.willCancelCrop = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEventListener('keydown', handleKeyDown);

  useEffect(() => {
    cropRectRef.current = cropRect;
  }, [cropRect]);

  useEffect(() => {}, [scale]);

  useDebounceEffect(
    () => {
      // scale 变化之后，crop窗口等比缩放
      if (!prevScale) prevScale = scale;
      if (prevScale === scale) return;

      const scaleDiff = scale / prevScale;
      prevScale = scale;
      const rect = {
        key: Date.now(),
        x: cropRectRef.current.x * scaleDiff,
        y: cropRectRef.current.y * scaleDiff,
        width: cropRectRef.current.width * scaleDiff,
        height: cropRectRef.current.height * scaleDiff,
      };
      setCropRect(rect);

      setHideCropper(true);
      clearTimeout(cropperTimmer);

      cropperTimmer = setTimeout(() => {
        setHideCropper(false);
      }, 100);
    },
    [scale],
    {
      wait: 100,
    },
  );

  useEffect(() => {
    tempRef.current.id = element.id;
    tempRef.current.width = width;
    tempRef.current.height = height;
  }, [element.id, width, height]);

  useEffect(() => {
    const handleOnExit = () => {
      tempRef.current.willCancelCrop = true;
    };

    eventBus.on('event_exitImageTool', handleOnExit);

    return () => {
      handleApplyCrop();
      eventBus.off('event_exitImageTool', handleOnExit);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ImageCropToolInner
      key={cropRect.key}
      hideCropper={hideCropper}
      element={element}
      width={width}
      height={height}
      cropRect={cropRect}
      onCropRectChange={setCropRect}
    />
  );
});

export const ImageCropToolInner = React.memo(({ hideCropper, cropRect, onCropRectChange, element, width, height }) => {
  const rndRef = useRef(null);
  const cropInnerWindowRef = useRef(null);
  const shiftPressing = useTempState('shiftPressing');
  const [loaded, setLoaded] = useState(false);

  // const appendedClassName = canvasInteracting ? 'pointer-events-none' : 'pointer-events-auto';
  const appendedClassName = 'pointer-events-auto';

  const handleDrag = useCallback((_, pos) => {
    const { x, y } = pos;
    const { clientWidth, clientHeight } = pos.node;
    onCropRectChange({ x, y, width: clientWidth, height: clientHeight });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResize = useCallback((_, __, target, ____, pos) => {
    const { x, y } = pos;
    const { clientWidth, clientHeight } = target;
    onCropRectChange({ x, y, width: clientWidth, height: clientHeight });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clipPath = useMemo(() => {
    const { x, y, width: cropWidth, height: cropHeight } = cropRect;
    return `inset(${y}px ${width - cropWidth - x}px ${height - cropHeight - y}px ${x}px)`;
  }, [cropRect, width, height]);

  const linesStyle = useMemo(() => {
    return {
      top: cropRect.y,
      left: cropRect.x,
      width: cropRect.width,
      height: cropRect.height,
      scaleX: 1,
      scaleY: 1,
    };
  }, [cropRect]);

  const handleLoad = useCallback(() => setLoaded(true), [setLoaded]);

  return (
    <>
      <div id="image-crop-tool" className={`absolute inset-0 ${loaded ? 'bg-black/60 ' : ''} ${appendedClassName}`}>
        <div
          ref={cropInnerWindowRef}
          style={{ width, height, clipPath }}
          className="absolute pointer-events-none overflow-hidden"
        >
          <img src={element.url} className="object-cover" style={{ width, height }} onLoad={handleLoad} />
        </div>
        <div style={linesStyle} className="absolute pointer-events-none border borer-white">
          <div className="absolute inset-0 flex items-stretch">
            <div className="flex-1" />
            <div className="flex-1 border-l border-white" />
            <div className="flex-1 border-l border-white" />
          </div>
          <div className="absolute inset-0 flex flex-col items-stretch">
            <div className="flex-1" />
            <div className="flex-1 border-t border-white" />
            <div className="flex-1 border-t border-white" />
          </div>
        </div>
        {!hideCropper && (
          <Rnd
            enableResizing
            ref={rndRef}
            default={{ x: linesStyle.left, y: linesStyle.top, width: linesStyle.width, height: linesStyle.height }}
            bounds={'parent'}
            maxWidth={width}
            maxHeight={height}
            onDrag={handleDrag}
            onResize={handleResize}
            lockAspectRatio={shiftPressing}
            className="pro-tool-image-crop-tool"
            resizeHandleWrapperClass="resize-handles"
            resizeHandleClasses={resizeHandleClasses}
          >
            <div style={{ width, height }} className="pointer-events-none" />
          </Rnd>
        )}
      </div>
    </>
  );
});
